import { Grid, Typography } from '@material-ui/core';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';
import BaseButton from 'src/common/components/base-button';
import { IKeypadModal, KeypadModal } from 'src/common/components/keypad-modal/keypad-modal';
import {
  popEmeraldModal,
  pushEmeraldModal,
} from 'src/common/emerald-modal-manager/emerald-modal-manager';
import { EmeraldDialogTypes } from 'src/common/enumerations/emerald-dialog-types';
import { IButton } from 'src/common/interfaces/IButton';
import { ScannedDataHandler } from 'src/devices/scanner-operations';
import Keypad from 'src/keypad/components/keypad';
import barcode from 'src/item-list/media/barcode.svg';
import { convertUnitPriceCurrency } from 'src/common/helpers/currency-helper';
import { styles } from './online-item-id-modal-styles';
import { ILineItemAddData } from 'src/items-list/business-logic/line-item-add';

type Props = AuthorizeOnlineItemIdModalProps;

const AUTHORIZE_ONLINE_ITEMID_MODAL = 'Authorize-Online-ItemId-Modal';
export const showAuthorizeOnlineItemIdModal = (props: Props, onVerify: ScannedDataHandler) => {
  pushEmeraldModal(
    <AuthorizeOnlineItemIdModal {...props} />,
    onVerify,
    AUTHORIZE_ONLINE_ITEMID_MODAL,
  );
};

export const closeAuthorizeOnlineItemIdModal = () => {
  popEmeraldModal(AUTHORIZE_ONLINE_ITEMID_MODAL);
};

const AuthorizeOnlineItemIdModal: React.FC<Props> = ({
  lineItemData,
  onClose,
  onVerify,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const classes = styles();

  const keypadSubmitButton: IButton = {
    label: t('Ok'),
    testId: 'rescan-external-item-id',
    onClick: (fieldText: string) => {
      onVerify(fieldText);
    },
  };

  const itemPrice = lineItemData.itemLookup.regularSalesUnitPrice?.price;
  const unitPrice = convertUnitPriceCurrency(itemPrice?.value || 0, itemPrice?.currency).split('/');
  const keypadModalProps: IKeypadModal = {
    onClose: onClose,
    title: 'OnlineItem',
    iconType: EmeraldDialogTypes.Warning,
    modalWidth: 64.715,
    modalHeight: 78.646,
    buttonComponent: (
      <Grid item container className={classes.authorizeOnlineItemIdCancelButton}>
        <Grid item>
          <BaseButton
            buttonType='white'
            className={classes.cancelButton}
            data-testid='cancel-button-online-id-modal'
            onClick={onClose}
          >
            <span className='label'>{t('Cancel')}</span>
          </BaseButton>
        </Grid>
      </Grid>
    ),
    contentArea: (
      <Grid className={classes.inner}>
        <Grid>
          <Typography
            variant='h2'
            data-testid='authorize-title-text'
            className={classes.authorizeTitle}
          >
            {t('OnlineItemExternalId_ScanData')}
          </Typography>
        </Grid>
        <Grid container className={classes.barPadding}>
          <div className={clsx(classes.barError, classes.bar)} />
        </Grid>
        <Grid>
          <Typography
            data-testid='scan-online-item-id-text'
            variant='body1'
            className={classes.authorizeIdText}
          >
            {t('OnlineItemExternalId_Activate')}
          </Typography>
        </Grid>
        <Grid
          container
          data-testid='barcode-pinpad-icon-container'
          className={classes.iconContainer}
        >
          <Grid className={classes.imageContainer}>
            <img src={barcode} alt='Barcode' />
          </Grid>
        </Grid>
        <Grid className={classes.itemContainer}>
          <Typography
            variant='h6'
            className={classes.itemName}
            data-testid='online-item-description'
          >
            {lineItemData.itemLookup.description}
          </Typography>
          {itemPrice?.value && (
            <Grid className={classes.priceContainer}>
              <Typography variant='h6' data-testid='online-item-price'>
                {unitPrice[0]}
              </Typography>
              <Typography
                variant='body2'
                className={classes.itemUnit}
                data-testid='online-item-unit'
              >
                {`/ ${unitPrice[1]}`}
              </Typography>
            </Grid>
          )}
        </Grid>
        <Typography variant='body1' className={classes.itemId}>
          {lineItemData.itemLookup.itemId}
        </Typography>
      </Grid>
    ),
    keypad: (
      <Keypad
        disableManualWeight={true}
        disableQuantity={true}
        disableDecimalInput={true}
        visible={true}
        topLabelText={''}
        keypadTestIdPrefix={'input-additional-scan-data'}
        submitButton={keypadSubmitButton}
      />
    ),
  };
  return <KeypadModal {...keypadModalProps} />;
};
export interface AuthorizeOnlineItemIdModalProps {
  onClose: () => void;
  onVerify: (verifyBarcode: string) => void;
  lineItemData: ILineItemAddData;
}

export default AuthorizeOnlineItemIdModal;
